(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 1000px)';
  var CLOSE_DELAY = 150;

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  function MegaNav(root) {
    this.root = root;
    this.trigger = root.dataset.trigger === 'click' ? 'click' : 'hover';
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-meganav-item]'));
    this.accordions = Array.prototype.slice.call(root.querySelectorAll('[data-meganav-accordion]'));
    this.overlay = root.querySelector('[data-meganav-overlay]');
    this.openItem = null;
    this.closeTimeout = null;

    this._bind();
    this._syncBreakpoint();
  }

  MegaNav.prototype._panelOf = function (item) {
    return item.querySelector('[data-meganav-panel]');
  };

  MegaNav.prototype._toggleOf = function (item) {
    return item.querySelector('[data-meganav-toggle]');
  };

  MegaNav.prototype.open = function (item) {
    var panel = this._panelOf(item);
    if (!panel || this.openItem === item) return;

    this.close();

    panel.hidden = false;
    item.classList.add('is-open');
    this._toggleOf(item).setAttribute('aria-expanded', 'true');
    this.openItem = item;
    this._sizePanel();

    if (!isDesktop() && this.overlay) this.overlay.hidden = false;
  };

  MegaNav.prototype.close = function () {
    if (!this.openItem) return;

    var panel = this._panelOf(this.openItem);
    if (panel) panel.hidden = true;

    this.openItem.classList.remove('is-open');
    this._toggleOf(this.openItem).setAttribute('aria-expanded', 'false');
    this.openItem = null;

    if (this.overlay) this.overlay.hidden = true;
  };

  /* The mobile sidebar and its scrim are fixed, so they need to know where the
     tab strip ends. The header is sticky, so this moves as the page scrolls. */
  MegaNav.prototype._sizePanel = function () {
    if (isDesktop()) {
      this.root.style.removeProperty('--meganav-panel-top');
      return;
    }

    var bottom = this.root.querySelector('.meganav__root').getBoundingClientRect().bottom;
    this.root.style.setProperty('--meganav-panel-top', Math.max(0, bottom) + 'px');
  };

  MegaNav.prototype.toggle = function (item) {
    if (this.openItem === item) {
      this.close();
    } else {
      this.open(item);
    }
  };

  MegaNav.prototype._cancelClose = function () {
    window.clearTimeout(this.closeTimeout);
  };

  MegaNav.prototype._scheduleClose = function () {
    var self = this;
    this._cancelClose();
    this.closeTimeout = window.setTimeout(function () {
      self.close();
    }, CLOSE_DELAY);
  };

  /* On desktop the column titles are plain headings; on mobile they are the
     accordion controls for their own column. */
  MegaNav.prototype._syncBreakpoint = function () {
    var desktop = isDesktop();

    this.accordions.forEach(function (button) {
      var panel = button.nextElementSibling;

      button.disabled = desktop;

      if (desktop) {
        button.removeAttribute('aria-expanded');
        if (panel) panel.hidden = false;
      } else {
        button.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      }
    });

    this.close();
    this._sizePanel();
  };

  MegaNav.prototype._bind = function () {
    var self = this;

    this.items.forEach(function (item) {
      var toggle = self._toggleOf(item);

      if (toggle) {
        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          self._cancelClose();
          self.toggle(item);
        });
      }

      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType !== 'mouse') return;
        if (!isDesktop() || self.trigger !== 'hover') return;

        self._cancelClose();

        /* Items without a panel still have to dismiss whatever is open. */
        if (self._panelOf(item)) {
          self.open(item);
        } else {
          self.close();
        }
      });
    });

    /* Panels live inside their item, so entering one keeps the nav "entered"
       even though it is painted below the header. */
    this.root.addEventListener('pointerenter', function () {
      self._cancelClose();
    });

    this.root.addEventListener('pointerleave', function (event) {
      if (event.pointerType !== 'mouse') return;
      if (!isDesktop() || self.trigger !== 'hover') return;
      self._scheduleClose();
    });

    this.accordions.forEach(function (button) {
      button.addEventListener('click', function () {
        var panel = button.nextElementSibling;
        if (!panel) return;

        var expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.close();
      });
    }

    document.addEventListener('click', function (event) {
      if (!self.root.contains(event.target)) self.close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !self.openItem) return;

      var toggle = self._toggleOf(self.openItem);
      self.close();
      if (toggle) toggle.focus();
    });

    /* Tabbing out of an open panel closes it. */
    this.root.addEventListener('focusout', function (event) {
      if (!self.openItem) return;
      if (event.relatedTarget && self.root.contains(event.relatedTarget)) return;
      self.close();
    });

    var resize = function () {
      self._sizePanel();
    };

    window.addEventListener('resize', resize);
    window.addEventListener('scroll', resize, { passive: true });

    var mediaQuery = window.matchMedia(DESKTOP_QUERY);
    var onChange = function () {
      self._syncBreakpoint();
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
    } else {
      mediaQuery.addListener(onChange);
    }
  };

  function init() {
    document.querySelectorAll('[data-meganav]').forEach(function (root) {
      if (root.dataset.meganavReady) return;
      root.dataset.meganavReady = 'true';
      new MegaNav(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-initialise after a theme editor section reload. */
  document.addEventListener('shopify:section:load', init);
})();

function setTrustpilotTheme(theme) {
  const widget = document.querySelector('.trustpilot-widget');
  if (!widget) return;

  widget.setAttribute('data-theme', theme);

  if (window.Trustpilot) {
    window.Trustpilot.loadFromElement(widget, true);
  }
}

setTrustpilotTheme('dark');
